import type { ReactNode } from "react";
import type { PayableStatus } from "../api/treasuryPayables";

// Presentational CRM primitives for the Treasury Ledger Payout v1
// read-only dashboard. No funds move, no network, no state — these
// only render the BE-computed values (money / status / action). Inline
// styles match the rest of the admin shell (CSS custom properties from
// the global theme).

// ─── Status chips ───────────────────────────────────────────────────

// CRM display status. `available` is a FE-only refinement of the BE `ready`
// status for a partner that has a payable BALANCE but no actionable payout
// request yet (the partner must request a withdrawal first). The BE reports
// such rows as `ready`; showing "Ready to pay" there misled operators, so the
// CRM renders them as "Available".
export type CrmDisplayStatus = PayableStatus | "available";

// The LABELS are governance and are pinned by tests both positively and
// negatively — a balance with no request is "Available", never "Ready to pay"
// (`PartnerPayables.test.tsx:116-128`, `PartnerPayableDetail.test.tsx:144-146`).
// Only the colours moved: they were the last literal hexes in the shared CRM
// vocabulary, tuned for the old ground and unable to follow a token repoint or
// carry the scoped light palette.
const STATUS_META: Record<
  CrmDisplayStatus,
  { label: string; fg: string; bg: string; border: string }
> = {
  ready: { label: "Ready to pay", fg: "var(--ok)", bg: "var(--ok-bg)", border: "var(--ok-border)" },
  available: {
    label: "Available",
    fg: "var(--accent)", bg: "var(--accent-bg)", border: "var(--accent-border)",
  },
  pending: {
    label: "Pending",
    fg: "var(--warn)", bg: "var(--warn-bg)", border: "var(--warn-border)",
  },
  paid: { label: "Paid", fg: "var(--info)", bg: "var(--info-bg)", border: "var(--info-border)" },
  blocked: {
    label: "Blocked",
    fg: "var(--danger)", bg: "var(--danger-bg)", border: "var(--danger-border)",
  },
  none: {
    label: "Nothing due",
    fg: "var(--fg-dim)", bg: "var(--neutral-bg)", border: "var(--neutral-border)",
  },
};

export function StatusChip({ status }: { status: CrmDisplayStatus }) {
  const meta = STATUS_META[status] ?? STATUS_META.none;
  return (
    <span
      data-status={status}
      style={{
        display: "inline-block",
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.04,
        padding: "2px 10px",
        borderRadius: "var(--r-pill)",
        color: meta.fg,
        background: meta.bg,
        border: `1px solid ${meta.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {meta.label}
    </span>
  );
}

// ─── Money rendering: human first, raw secondary ────────────────────

export function MoneyValue({
  display,
  raw,
  size = 22,
  tone,
}: {
  display: string;
  raw?: string;
  size?: number;
  tone?: string;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: size,
          fontWeight: 600,
          lineHeight: 1.15,
          letterSpacing: -0.02,
          color: tone ?? "var(--fg)",
          // Sans with tabular figures, NOT monospace. Fixed-advance digits are
          // what makes a column of money line up; the monospace FACE is what
          // makes it read as log output. `tabular-nums` gives the first
          // without the second, so a treasury figure reads as a number a
          // person owns rather than as a value a process printed. The exact
          // machine integer underneath stays mono — that one gets copied.
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {display}
      </div>
      {raw && (
        <div
          className="g-mono"
          style={{ marginTop: 3, fontSize: 11.5, color: "var(--fg-dim)" }}
        >
          {raw}
        </div>
      )}
    </div>
  );
}

// ─── Square/rounded metric card ─────────────────────────────────────

export function MetricCard({
  title,
  accent,
  badge,
  children,
  footer,
  cta,
}: {
  title: string;
  accent?: string;
  badge?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  cta?: { label: string; to: string } | null;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        border: "1px solid var(--border)",
        borderTop: `2px solid ${accent ?? "var(--accent)"}`,
        borderRadius: "var(--r-lg)",
        padding: "16px 18px",
        background: "var(--bg-card)",
        boxShadow: "var(--shadow-1)",
        minHeight: 132,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <h3
          style={{
            margin: 0,
            fontSize: 11.5,
            fontWeight: 650,
            textTransform: "uppercase",
            letterSpacing: 0.06,
            color: "var(--fg-dim)",
          }}
        >
          {title}
        </h3>
        {badge && <span style={{ marginLeft: "auto" }}>{badge}</span>}
      </div>
      <div>{children}</div>
      {footer && (
        <div style={{ fontSize: 12.5, color: "var(--fg-dim)", lineHeight: 1.45 }}>
          {footer}
        </div>
      )}
      {cta && (
        <a
          href={cta.to}
          style={{
            marginTop: "auto",
            paddingTop: 4,
            fontSize: 12.5,
            fontWeight: 620,
            color: accent ?? "var(--accent)",
            textDecoration: "none",
          }}
        >
          {cta.label} →
        </a>
      )}
    </div>
  );
}

// ─── Reusable loading / error blocks ────────────────────────────────

export function LoadingBlock({ label = "Loading…" }: { label?: string }) {
  return (
    <div
      role="status"
      style={{
        margin: "12px 0",
        padding: "28px 16px",
        border: "1px solid var(--border)",
        borderRadius: "var(--r-lg)",
        background: "var(--bg-card)",
        textAlign: "center",
        color: "var(--fg-dim)",
        fontSize: 13,
      }}
    >
      {label}
    </div>
  );
}

export function ErrorBlock({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div
      role="alert"
      style={{
        margin: "12px 0",
        padding: "12px 14px",
        border: "1px solid var(--danger-border)",
        background: "var(--danger-bg)",
        color: "var(--danger)",
        borderRadius: "var(--r-md)",
        fontSize: 13,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 10,
      }}
    >
      <span>{message}</span>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          style={{ marginLeft: "auto", padding: "4px 12px", fontSize: 12 }}
        >
          Retry
        </button>
      )}
    </div>
  );
}
